import { useEffect, useRef } from "react";
import { isTalkbackPublication } from "./talkback";
import { isDirectorOnAirPublication } from "./directorMic";

// Talent conversation audio (LSC-530, ADR 0218). Multi-guest CaveCasts had no shared
// talent-audio environment: the Legend and Guests could each publish and be switched
// to Program, but nobody could HEAR anyone else — the room behaved like isolated
// contribution feeds into a switcher, not a live conversation. This hook gives every
// talent surface (Legend Go-Live + each Guest join) the missing bus.
//
// The product rule (Doug): **talent conversation and Fan Program audio are separate
// concerns.** Going on/off Program changes what the FAN receives, not whether talent
// can hear one another. So:
//   • Talent plays the microphone audio of every OTHER talent, on OR off Program.
//   • Fans are unaffected: ConsumerWatch still attaches ONLY the on-program source
//     audio. Fans never publish (viewer tokens are subscribe-only, ADR 0044), so they
//     expose no audio track here and are excluded by construction — there is no path
//     by which this hook could leak a fan mic into anything.
//   • The Director's talkback mic (ADR 0213) is EXCLUDED by its reserved track name,
//     so the always-on conversation never double-plays the private PTT return path.
//     The Director's PUBLIC on-air mic (`director-onair`, LSC-633/ADR 0247) is likewise
//     EXCLUDED here — it has its own `useDirectorOnAirAudio` attach path on the talent
//     surfaces, so this bus must not double-play it.
//   • The Director is not part of this bus — they monitor via the audio mixer
//     (ADR 0085), address talent privately via talkback (ADR 0213), and join the public
//     mix via the separate on-air mic (ADR 0247). The Director's own tracks are skipped.
//
// Mechanics: LiveKit auto-subscribes remote tracks (default autoSubscribe), so the
// tracks are already flowing — the gap was purely that the publisher surfaces never
// ATTACHED them to an audio element. For each remote AUDIO publication that is not
// talkback we `track.attach()` a fresh hidden <audio> into a container, and detach +
// remove it on unsubscribe / teardown. Clean teardown means a disconnecting participant
// leaves no orphaned audio (acceptance #9). Like talkback.tsx this keeps a structural
// room/track shape so a jsdom test fake exercises the attach/detach bookkeeping; the
// real audible output is beta QA (jsdom can't run WebRTC audio).

/** Structural shapes — just enough of LiveKit's Room/track surface for the hook and a
 *  test fake, kept tolerant of the fields LiveKit actually exposes. */
interface TalentTrack {
  kind?: string; // "audio" | "video"
  sid?: string;
  attach?: (el?: HTMLMediaElement) => HTMLMediaElement | void;
  detach?: (el?: HTMLMediaElement) => void;
}
interface TalentPublication {
  trackName?: string;
  name?: string;
  kind?: string; // "audio" | "video" on the publication
  trackSid?: string;
  sid?: string;
  track?: TalentTrack | null;
  isSubscribed?: boolean;
}
interface TalentParticipant {
  trackPublications?: Map<string, TalentPublication> | TalentPublication[];
  audioTrackPublications?: Map<string, TalentPublication> | TalentPublication[];
}
interface TalentRoom {
  on(event: string, cb: (...args: unknown[]) => void): unknown;
  off?(event: string, cb: (...args: unknown[]) => void): unknown;
  remoteParticipants?: Map<string, TalentParticipant> | TalentParticipant[];
}

/** True when a remote publication is a talent conversation feed this surface should
 *  play: an AUDIO track that is NOT one of the Director's mics — neither the private
 *  talkback (ADR 0213) nor the public on-air mic (ADR 0247), each of which has its own
 *  dedicated attach path. (Video publications are skipped too.) Pure + unit-tested. */
export function isTalentAudio(
  pub: { trackName?: string; name?: string; kind?: string } | undefined | null,
  trackKind?: string,
): boolean {
  if (!pub) return false;
  const kind = trackKind ?? pub.kind;
  return kind === "audio" && !isTalkbackPublication(pub) && !isDirectorOnAirPublication(pub);
}

function iterValues<T>(coll: Map<string, T> | T[] | undefined): T[] {
  if (!coll) return [];
  if (coll instanceof Map) return Array.from(coll.values());
  if (Array.isArray(coll)) return coll;
  return [];
}

function pubKey(pub: TalentPublication, track?: TalentTrack | null): string {
  return pub.trackSid ?? pub.sid ?? track?.sid ?? pub.trackName ?? pub.name ?? "";
}

/** Talent conversation audio (LSC-530): play every OTHER talent's microphone into a
 *  hidden container so the Legend and Guests hear one another independent of Program.
 *  Returns a `containerRef` the surface renders as an aria-hidden <div>. Fans and the
 *  Director's talkback are excluded (see the module note). Idempotent + teardown-safe. */
export function useTalentAudio(room: TalentRoom | null): {
  containerRef: React.RefObject<HTMLDivElement | null>;
} {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const els = useRef<Map<string, { el: HTMLMediaElement; track: TalentTrack }>>(new Map());

  useEffect(() => {
    if (!room) return;
    const play = (track: TalentTrack | undefined | null, pub: TalentPublication | undefined) => {
      if (!track || !pub || !isTalentAudio(pub, track.kind) || !track.attach) return;
      const k = pubKey(pub, track);
      if (!k || els.current.has(k)) return; // idempotent — never double-attach a track
      try {
        const el = track.attach(); // no element arg → LiveKit creates + plays a fresh <audio>
        if (el) {
          el.autoplay = true;
          containerRef.current?.appendChild(el);
          els.current.set(k, { el, track });
        }
      } catch {
        // jsdom / detached element — audio is cosmetic under test
      }
    };
    const stop = (track: TalentTrack | undefined | null, pub: TalentPublication | undefined) => {
      if (!pub) return;
      const k = pubKey(pub, track ?? undefined);
      const entry = els.current.get(k);
      if (!entry) return;
      try {
        entry.track.detach?.(entry.el);
      } catch {
        /* best-effort */
      }
      try {
        entry.el.remove();
      } catch {
        /* best-effort */
      }
      els.current.delete(k);
    };

    const onSub = (...args: unknown[]) => play(args[0] as TalentTrack, args[1] as TalentPublication);
    const onUnsub = (...args: unknown[]) => stop(args[0] as TalentTrack, args[1] as TalentPublication);

    // Attach tracks already subscribed before this surface mounted (a talent that
    // joins AFTER others — the missed-event case the live listeners don't cover).
    for (const p of iterValues<TalentParticipant>(room.remoteParticipants)) {
      const pubs = iterValues<TalentPublication>(p.audioTrackPublications ?? p.trackPublications);
      for (const pub of pubs) {
        if (pub.track && (pub.isSubscribed ?? true)) play(pub.track, pub);
      }
    }

    room.on("trackSubscribed", onSub);
    room.on("trackUnsubscribed", onUnsub);
    const bookkeeping = els.current;
    return () => {
      room.off?.("trackSubscribed", onSub);
      room.off?.("trackUnsubscribed", onUnsub);
      // Teardown: detach + remove every element so a room swap leaves no orphaned audio.
      for (const { el, track } of bookkeeping.values()) {
        try {
          track.detach?.(el);
        } catch {
          /* best-effort */
        }
        try {
          el.remove();
        } catch {
          /* best-effort */
        }
      }
      bookkeeping.clear();
    };
  }, [room]);

  return { containerRef };
}
